import type { FunnelId } from "@repo/app-types/entities/funnel";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { SimpleModalVariant } from "@repo/ui/Modal/modal";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import type { FC } from "react";
import {
  useFunnelQuery,
  useRetryFunnelInductionMutation,
} from "../../../api/entities/funnel.js";
import { useTeammateAuthorization } from "../../../helpers/teammateAuthorizationContext.js";

/*
 * The states a funnel shows before it's ready, shared by the agent's Funnel
 * view and the playbook's Funnel section.
 */

/** Mounted only while the funnel is pending, so its polling stops once it isn't. */
export const FunnelInductionPending: FC<{
  funnelId: FunnelId;
  /** What induction reads, such as "this agent's recent interactions". */
  subjectDescription: string;
}> = (props) => {
  const { subjectDescription } = props;
  useFunnelQuery(props.funnelId, { pollingInterval: 5000 });

  return (
    <div className="flex flex-col items-center gap-3 rounded-xl bg-white px-4 py-12 text-center shadow-xs ring-1 ring-black/5">
      <Loader style={LoaderStyle.ZOOMIES} />
      <div className="text-sm font-semibold text-foreground">
        Generating the funnel
      </div>
      <p className="max-w-md text-sm text-muted-foreground">
        We&apos;re reading {subjectDescription} to find the stages and moments.
        This takes a few minutes; this page updates on its own.
      </p>
    </div>
  );
};

export const FunnelInductionFailedBanner: FC<{
  funnelId: FunnelId;
  inductionError: string | null;
  /** Whether to offer Retry. */
  isWritable: boolean;
}> = (props) => {
  const { funnelId, inductionError, isWritable } = props;

  const [retryInduction, { isLoading: isRetrying }] =
    useRetryFunnelInductionMutation();
  const canEdit = useTeammateAuthorization().can(
    PermissionActions.UPDATE,
    PermissionSubjects.TEAMMATES,
  );

  const handleRetry = async () => {
    try {
      await retryInduction(funnelId).unwrap();
    } catch (error) {
      console.error("Failed to retry funnel induction:", error);
      showToast({
        variant: ToastVariant.Error,
        title: "Couldn't retry",
        description: "Please try again.",
      });
    }
  };

  return (
    <InfoBanner
      variant={SimpleModalVariant.Warning}
      title="We couldn't generate the funnel"
      description={inductionError ?? "Something went wrong."}
      actions={
        isWritable ? (
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => void handleRetry()}
            isLoading={isRetrying}
            isDisabled={!canEdit}
          >
            Retry
          </Button>
        ) : null
      }
    />
  );
};
